/**
 * Orchestrator that wires a {@link PushProvider} to a
 * {@link PushSubscriptionRegistrar}.
 *
 * Two-phase lifecycle (mirrors the iOS and Android counterparts in
 * Modules/Swift/MeiucaPush and Modules/Kotlin/push):
 *
 *   1. `prepare()` — runs at app launch, regardless of auth. Configures the
 *      SDK, asks for permission, captures the token, and immediately
 *      forwards it to the registrar. Pre-auth, the registrar reaches the
 *      backend without a bearer — the backend creates an anonymous
 *      subscription tied to the device.
 *   2. `enableBackendRegistration()` — call after the user authenticates.
 *      Re-sends the captured token so the backend claims this device for
 *      the user. Idempotent: the backend skips no-op same-user claims and
 *      preserves any user already on the row.
 *   3. `disableBackendRegistration()` — kept for symmetry with future
 *      logout-with-unsubscribe flows. Currently a no-op: the row stays
 *      bound to the last user until another user logs in and claims it.
 *
 * Token refreshes always re-register with whatever auth state the registrar
 * observes at the time of the call.
 */

import { presentSystemNotification, type ForegroundPresenter } from './foregroundPresenter';
import type { PushProvider } from './provider';
import type { PushSubscriptionRegistrar } from './registrar';
import type { PushToken } from './types';

export interface PushNotificationsServiceOptions {
  provider: PushProvider;
  registrar: PushSubscriptionRegistrar;
  /**
   * O que fazer com notificação que chega com a aba em foco. Por padrão,
   * exibe uma notificação do sistema — sem isso ela chegaria e não apareceria,
   * porque o SDK só entrega o payload e não desenha nada nesse caso.
   */
  foregroundPresenter?: ForegroundPresenter;
  /**
   * Source of the stable per-install identifier. Composition roots should
   * back this with durable storage (a UUID persisted in `localStorage` /
   * `IndexedDB`) so the value survives reloads and token rotations.
   */
  deviceIdentifierProvider: () => string;
}

export class PushNotificationsService {
  private readonly provider: PushProvider;
  private readonly registrar: PushSubscriptionRegistrar;
  private readonly deviceIdentifierProvider: () => string;
  private readonly foregroundPresenter: ForegroundPresenter;

  private prepared = false;
  private inFlight: Promise<void> | undefined;
  private lastCapturedToken: PushToken | undefined;
  private unsubscribe?: () => void;
  private unsubscribeForeground?: () => void;

  constructor(options: PushNotificationsServiceOptions) {
    this.provider = options.provider;
    this.registrar = options.registrar;
    this.deviceIdentifierProvider = options.deviceIdentifierProvider;
    this.foregroundPresenter = options.foregroundPresenter ?? presentSystemNotification;
  }

  /**
   * Configure the SDK, ask for notification permission, capture the device
   * token and immediately forward it to the registrar (anonymously when no
   * session is available). Independent of authentication — safe to call at
   * app launch.
   *
   * Idempotent, but only settles once permission is actually granted: a
   * launch-time call that the browser refused for lack of user activation
   * leaves the service retryable, so a later call from a click still runs.
   * Concurrent calls share the same in-flight promise.
   */
  async prepare(): Promise<void> {
    if (this.prepared) return;
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.runPrepare().finally(() => {
      this.inFlight = undefined;
    });

    return this.inFlight;
  }

  private async runPrepare(): Promise<void> {
    try {
      await this.provider.configure();

      const status = await this.provider.requestAuthorization();
      if (status !== 'granted' && status !== 'provisional') {
        // Deliberately does NOT latch `prepared`. Browsers that require user
        // activation for the permission prompt (Firefox, Safari) reject the
        // launch-time call, and the user's later click — the one moment a
        // gesture exists — has to be able to try again. Latching here made
        // that click a silent no-op.
        return;
      }

      this.prepared = true;

      const initial = await this.provider.currentToken();
      if (initial) {
        await this.captureToken(initial);
      }

      this.unsubscribe = this.provider.onTokenUpdate((token) => {
        void this.captureToken(token);
      });

      this.unsubscribeForeground = this.provider.onForegroundMessage?.((message) => {
        void this.foregroundPresenter(message);
      });
    } catch {
      // configure() or requestAuthorization() threw — fail silently. Apps
      // that need logging can wrap the provider or registrar.
    }
  }

  /**
   * Re-send the captured token so the backend claims this device for the
   * authenticated user. No-op when no token has been captured yet — the
   * next capture will register naturally.
   */
  async enableBackendRegistration(): Promise<void> {
    if (this.lastCapturedToken) {
      await this.safeRegister(this.lastCapturedToken);
    }
  }

  /** Logout hook. Currently a no-op on the backend. */
  disableBackendRegistration(): void {
    // No backend side-effect on this slice.
  }

  /**
   * Cancel the token-refresh subscription. After `stop()`, `prepare()` can
   * be called again to restart.
   */
  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.unsubscribeForeground?.();
    this.unsubscribeForeground = undefined;
    this.prepared = false;
  }

  private async captureToken(token: PushToken): Promise<void> {
    this.lastCapturedToken = token;
    await this.safeRegister(token);
  }

  private async safeRegister(token: PushToken): Promise<void> {
    try {
      await this.registrar.register(token, this.deviceIdentifierProvider());
    } catch {
      // Backend register failed — swallow. Apps that need retries can wrap
      // the registrar.
    }
  }
}
